import { useState, useEffect, useRef } from "react";
import type { ContentBlock } from "../../../mocks/classContent";

interface QuizState {
  selected: number | null;
  passed: boolean;
}

interface Props {
  content: ContentBlock[];
  onComplete: () => void;
}

function QuizCard({
  block,
  quizState,
  onAnswer,
}: {
  block: ContentBlock;
  quizState: QuizState;
  onAnswer: (quizId: string, index: number) => void;
}) {
  const { selected, passed } = quizState;

  return (
    <div
      className={`rounded-2xl border-2 p-5 transition-all duration-300 ${
        passed
          ? "border-[#00D873] bg-[#F0FDF7]"
          : selected !== null
          ? "border-red-300 bg-red-50"
          : "border-[#E8E9EB] bg-[#FAFBFC]"
      }`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div
          className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
            passed ? "bg-[#00D873]" : selected !== null ? "bg-red-400" : "bg-[#191F28]"
          }`}
        >
          {passed ? (
            <i className="ri-check-line text-white text-sm" />
          ) : selected !== null ? (
            <i className="ri-close-line text-white text-sm" />
          ) : (
            <i className="ri-question-line text-white text-xs" />
          )}
        </div>
        <span
          className={`text-xs font-bold tracking-widest uppercase ${
            passed ? "text-[#00D873]" : selected !== null ? "text-red-500" : "text-[#8B95A1]"
          }`}
        >
          {passed ? "정답!" : selected !== null ? "오답 — 다시 도전!" : "이해 퀴즈"}
        </span>
      </div>

      {/* Question */}
      <p className="text-[15px] font-bold text-[#191F28] leading-snug mb-4">
        {block.question}
      </p>

      {/* Options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {block.options?.map((opt, idx) => {
          const isSelected = selected === idx;
          const isCorrect = idx === block.answer;
          const showResult = selected !== null;

          let btnStyle = "bg-white border-2 border-[#E8E9EB] text-[#3D4756] hover:border-[#191F28]/30";
          if (showResult && isCorrect) {
            btnStyle = "bg-[#00D873] border-2 border-[#00D873] text-white";
          } else if (showResult && isSelected && !isCorrect) {
            btnStyle = "bg-red-100 border-2 border-red-400 text-red-600";
          } else if (!showResult) {
            btnStyle = "bg-white border-2 border-[#E8E9EB] text-[#3D4756] hover:border-[#00D873]/50 hover:bg-[#F0FDF7]";
          }

          return (
            <button
              key={idx}
              onClick={() => {
                if (block.quizId && !passed) onAnswer(block.quizId, idx);
              }}
              disabled={passed}
              className={`text-left px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer ${btnStyle} ${passed ? "cursor-default" : ""}`}
            >
              <span className="mr-2 opacity-50 text-xs">{["A", "B", "C", "D"][idx]}.</span>
              {opt}
            </button>
          );
        })}
      </div>

      {/* Explanation */}
      {passed && block.explanation && (
        <div className="mt-4 flex gap-2 bg-[#E8F5E9] rounded-xl p-3">
          <span className="text-base flex-shrink-0">💡</span>
          <p className="text-sm text-[#2D6A4F] font-medium leading-relaxed">{block.explanation}</p>
        </div>
      )}
      {selected !== null && !passed && (
        <p className="mt-3 text-sm text-red-500 font-medium">
          틀렸어요! 다른 보기를 선택해보세요.
        </p>
      )}
    </div>
  );
}

export default function LearnStep({ content, onComplete }: Props) {
  const [readProgress, setReadProgress] = useState(0);
  const [scrollPassed, setScrollPassed] = useState(false);
  const [tocOpen, setTocOpen] = useState(true);
  const [activeId, setActiveId] = useState<string>("");
  const [quizStates, setQuizStates] = useState<Record<string, QuizState>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const headingRefs = useRef<Record<string, HTMLElement | null>>({});

  // Build TOC items
  const tocItems = content
    .map((block, idx) => {
      if (block.type === "heading" || block.type === "heading2") {
        return { id: `section-${idx}`, text: block.text ?? "", level: block.type === "heading" ? 1 : 2 };
      }
      return null;
    })
    .filter(Boolean) as { id: string; text: string; level: number }[];

  // All quizzes in content
  const allQuizIds = content
    .filter((b) => b.type === "quiz" && b.quizId)
    .map((b) => b.quizId as string);

  const allQuizzesPassed =
    allQuizIds.length === 0 ||
    allQuizIds.every((id) => quizStates[id]?.passed === true);

  const canComplete = scrollPassed && allQuizzesPassed;
  const pendingQuizCount = allQuizIds.filter((id) => !quizStates[id]?.passed).length;

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const el = containerRef.current;
      const rect = el.getBoundingClientRect();
      const totalHeight = el.offsetHeight;
      const scrolled = Math.max(0, -rect.top + window.innerHeight * 0.5);
      const pct = Math.min(100, Math.round((scrolled / totalHeight) * 100));
      setReadProgress(pct);
      if (pct >= 70) setScrollPassed(true);

      let currentActive = "";
      for (const item of tocItems) {
        const elRef = headingRefs.current[item.id];
        if (elRef) {
          const top = elRef.getBoundingClientRect().top;
          if (top <= 120) currentActive = item.id;
        }
      }
      if (currentActive) setActiveId(currentActive);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [tocItems]);

  const scrollToSection = (id: string) => {
    const el = headingRefs.current[id];
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handleQuizAnswer = (quizId: string, selectedIdx: number) => {
    const block = content.find((b) => b.quizId === quizId);
    if (!block) return;
    const passed = selectedIdx === block.answer;
    setQuizStates((prev) => ({
      ...prev,
      [quizId]: { selected: selectedIdx, passed },
    }));
  };

  // Build heading id map
  const headingIndex: Record<number, string> = {};
  content.forEach((block, idx) => {
    if (block.type === "heading" || block.type === "heading2") {
      headingIndex[idx] = `section-${idx}`;
    }
  });

  return (
    <div className="bg-white rounded-2xl overflow-hidden mb-6" ref={containerRef}>
      {/* Reading progress bar */}
      <div className="h-1 bg-[#F2F4F6] sticky top-16 z-10">
        <div
          className="h-full bg-[#00D873] transition-all duration-300"
          style={{ width: `${readProgress}%` }}
        />
      </div>

      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-5 border-b border-[#F2F4F6]">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-book-open-2-line text-[#00D873] text-lg" />
            </div>
            <span className="text-xs font-bold text-[#00D873] tracking-widest uppercase">학습 자료</span>
          </div>
          <div className="flex items-center gap-3">
            {allQuizIds.length > 0 && (
              <span
                className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                  allQuizzesPassed
                    ? "bg-[#E8F5E9] text-[#00D873]"
                    : "bg-[#FFF3E0] text-[#FF8C00]"
                }`}
              >
                {allQuizzesPassed ? "퀴즈 완료" : `퀴즈 ${allQuizIds.length - pendingQuizCount}/${allQuizIds.length}`}
              </span>
            )}
            <span className="text-xs text-[#8B95A1] font-medium">{readProgress}% 읽음</span>
          </div>
        </div>

        {/* Table of Contents */}
        {tocItems.length > 0 && (
          <div className="mb-8 rounded-2xl border border-[#F2F4F6] overflow-hidden">
            <button
              onClick={() => setTocOpen((v) => !v)}
              className="w-full flex items-center justify-between px-5 py-4 bg-[#F8FAF9] hover:bg-[#F2F4F6] transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-list-check-2 text-[#191F28] text-base" />
                </div>
                <span className="text-sm font-bold text-[#191F28]">목차</span>
                <span className="text-xs text-[#8B95A1] font-medium ml-1">{tocItems.length}개 섹션</span>
              </div>
              <div className="w-5 h-5 flex items-center justify-center">
                <i
                  className={`ri-arrow-down-s-line text-[#8B95A1] text-lg transition-transform duration-200 ${
                    tocOpen ? "rotate-180" : ""
                  }`}
                />
              </div>
            </button>

            {tocOpen && (
              <div className="px-5 py-3 bg-white divide-y divide-[#F8FAF9]">
                {tocItems.map((item, i) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center gap-3 py-2.5 text-left cursor-pointer transition-colors group ${
                      activeId === item.id ? "text-[#00D873]" : "text-[#3D4756] hover:text-[#191F28]"
                    }`}
                  >
                    <span
                      className={`flex-shrink-0 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                        activeId === item.id
                          ? "bg-[#00D873] text-white"
                          : "bg-[#F2F4F6] text-[#8B95A1] group-hover:bg-[#E8F5E9] group-hover:text-[#00D873]"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className={`text-sm font-medium leading-snug ${item.level === 2 ? "pl-2 text-[13px]" : ""}`}>
                      {item.text}
                    </span>
                    {activeId === item.id && (
                      <span className="ml-auto w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-right-s-line text-[#00D873] text-sm" />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content Blocks */}
        <div className="space-y-6">
          {content.map((block, idx) => {
            const sectionId = headingIndex[idx];

            if (block.type === "heading") {
              return (
                <h2
                  key={idx}
                  id={sectionId}
                  ref={(el) => { headingRefs.current[sectionId] = el; }}
                  className="text-2xl font-extrabold text-[#191F28] leading-tight scroll-mt-24"
                >
                  {block.text}
                </h2>
              );
            }
            if (block.type === "heading2") {
              return (
                <h3
                  key={idx}
                  id={sectionId}
                  ref={(el) => { headingRefs.current[sectionId] = el; }}
                  className="text-lg font-bold text-[#191F28] mt-2 scroll-mt-24"
                >
                  {block.text}
                </h3>
              );
            }
            if (block.type === "paragraph") {
              return (
                <p key={idx} className="text-[#3D4756] text-[15px] leading-[1.85]">
                  {block.text}
                </p>
              );
            }
            if (block.type === "callout") {
              return (
                <div
                  key={idx}
                  className="rounded-xl p-4 text-sm leading-relaxed font-medium"
                  style={{ backgroundColor: block.bg ?? "#E8F5E9", color: "#191F28" }}
                >
                  {block.text}
                </div>
              );
            }
            if (block.type === "code") {
              return (
                <div key={idx} className="bg-[#191F28] rounded-xl p-4 overflow-x-auto">
                  <code className="text-[#00D873] text-sm font-mono leading-relaxed whitespace-pre-wrap">
                    {block.text}
                  </code>
                </div>
              );
            }
            if (block.type === "image") {
              return (
                <div key={idx} className="rounded-xl overflow-hidden">
                  <img
                    src={block.src}
                    alt={block.caption}
                    className="w-full h-[200px] object-cover object-top"
                  />
                  <p className="text-xs text-[#8B95A1] text-center mt-2 font-medium">{block.caption}</p>
                </div>
              );
            }
            if (block.type === "tip") {
              return (
                <div key={idx} className="flex gap-3 bg-[#FFF9E6] rounded-xl p-4">
                  <span className="text-xl flex-shrink-0">💡</span>
                  <p className="text-sm text-[#191F28] leading-relaxed font-medium">{block.text}</p>
                </div>
              );
            }
            if (block.type === "quiz" && block.quizId) {
              const state = quizStates[block.quizId] ?? { selected: null, passed: false };
              return (
                <QuizCard
                  key={idx}
                  block={block}
                  quizState={state}
                  onAnswer={handleQuizAnswer}
                />
              );
            }
            return null;
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 pt-6 border-t border-[#F2F4F6]">
          {!scrollPassed ? (
            <div className="text-center">
              <p className="text-sm text-[#8B95A1] mb-3">
                스크롤해서 학습 내용을 읽어보세요 ({readProgress}%)
              </p>
              <div className="w-full h-2 bg-[#F2F4F6] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#00D873]/40 rounded-full transition-all duration-300"
                  style={{ width: `${readProgress}%` }}
                />
              </div>
            </div>
          ) : !allQuizzesPassed ? (
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-question-answer-line text-[#FF8C00] text-base" />
                </div>
                <p className="text-sm font-bold text-[#191F28]">
                  퀴즈 {pendingQuizCount}개를 풀어야 다음 단계로 갈 수 있어요!
                </p>
              </div>
              <p className="text-xs text-[#8B95A1]">위 퀴즈를 맞춰보세요. 실제 이해도를 확인하는 중이에요.</p>
            </div>
          ) : (
            <button
              onClick={onComplete}
              className="w-full py-4 bg-[#00D873] text-white font-extrabold text-base rounded-2xl cursor-pointer hover:opacity-90 transition-all hover:-translate-y-0.5 whitespace-nowrap"
              style={{ boxShadow: "0 6px 24px rgba(0,216,115,0.3)" }}
            >
              완벽해요! 실습하러 가기 →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
